import React, { useState, useEffect, useRef, useSyncExternalStore } from 'react';
import styled, { css, createGlobalStyle } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MAX_MESSAGES,
  MAX_MESSAGE_LENGTH,
  getConfig,
  isParentOriginAllowed,
  resolveEndpoint,
  resolveIdentifyEndpoint,
  subscribe,
  updateConfigFromParent
} from './config';
import {
  extractAnswer,
  extractConversationId,
  extractGuestName,
  identifyGuest,
  sendWithRetry
} from './transport';

// The guest's name comes from a third party and is spliced into markdown, so
// neutralise anything markdown would interpret (links, emphasis, headings).
const escapeMarkdown = (text) => text.replace(/[\\`*_{}[\]()<>#+\-.!|~]/g, '\\$&');

// Upstream responses occasionally arrive with exotic whitespace and escaped
// newlines that confuse the markdown renderer. Normalise them without touching
// leading indentation, which markdown uses for nested lists and code blocks.
const normaliseResponseText = (text) => {
  if (!text) return text;

  let out = text.replace(/\r\n/g, '\n');

  // Non-breaking and typographic spaces render as unbreakable glyphs.
  out = out.replace(/[\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g, ' ');

  // Collapse runs of spaces *within* a line only: never leading indentation,
  // never the trailing double-space that markdown reads as a hard break.
  out = out.replace(/(\S)[ \t]{2,}(?=\S)/g, '$1 ');

  // Some responses contain the two characters \ and n instead of a newline.
  out = out.replace(/\\n/g, '\n');

  // A lone newline is a hard break, not a paragraph join. Written with a
  // capture group rather than a lookbehind, which Safari < 16.4 cannot parse.
  out = out.replace(/([^\n])\n(?!\n)/g, '$1  \n');

  return out;
};

const bubbleBackground = ({ $isSender, $isTest }) => {
  if ($isSender) return '#DCF8C6';
  if ($isTest) return '#E8F5E8';
  return '#ffffff';
};

const bubbleColor = ({ $isTest }) => ($isTest ? '#2E7D32' : '#000000');

// Shared bubble chrome. Kept in one place so the two bubble variants below
// cannot drift apart.
const bubble = css`
  background-color: ${bubbleBackground};
  color: ${bubbleColor};
  align-self: ${(props) => (props.$isSender ? 'flex-end' : 'flex-start')};
  padding: 12px 16px;
  border-radius: ${(props) =>
    props.$isSender ? '18px 18px 4px 18px' : '18px 18px 18px 4px'};
  max-width: 80%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow-wrap: break-word;
  hyphens: none;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
    Arial, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1.4;
  position: relative;

  /* WhatsApp-style message tail */
  &::before {
    content: '';
    position: absolute;
    ${(props) => (props.$isSender ? 'right: -8px;' : 'left: -8px;')}
    top: 0;
    width: 0;
    height: 0;
    border: 8px solid transparent;
    ${(props) =>
      props.$isSender
        ? `border-left-color: ${bubbleBackground(props)};`
        : `border-right-color: ${bubbleBackground(props)};`}
  }
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  /* Fill whatever the embedding page gives us rather than assuming the
     viewport height — this component is always rendered inside an iframe. */
  height: 100%;
  min-height: 0;
  background-color: #e5ddd5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
    Arial, sans-serif;
  position: relative;
`;

const MessageContainer = styled.div`
  flex: 1;
  /* Without min-height a flex child refuses to shrink below its content, which
     pushes the input row out of view. */
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #e5ddd5;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const Message = styled.div`
  ${bubble}
  white-space: pre-wrap;
`;

const MarkdownMessage = styled.div`
  ${bubble}

  h1, h2, h3, h4, h5, h6 {
    margin: 8px 0 4px 0;
    font-weight: 600;
    line-height: 1.3;
  }

  h1 {
    font-size: 18px;
  }
  h2 {
    font-size: 16px;
  }
  h3 {
    font-size: 15px;
  }
  h4,
  h5,
  h6 {
    font-size: 14px;
  }

  p {
    margin: 0 0 8px 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 2px 0;
  }

  code {
    background-color: rgba(0, 0, 0, 0.1);
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
  }

  pre {
    background-color: rgba(0, 0, 0, 0.1);
    padding: 8px 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
  }

  pre code {
    background-color: transparent;
    padding: 0;
  }

  blockquote {
    border-left: 3px solid #25d366;
    margin: 8px 0;
    padding-left: 12px;
    color: #666;
    font-style: italic;
  }

  a {
    color: #25d366;
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  strong {
    font-weight: 600;
  }

  em {
    font-style: italic;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 8px 0;
    display: block;
    overflow-x: auto;
  }

  th,
  td {
    border: 1px solid #ddd;
    padding: 6px 8px;
    text-align: left;
  }

  th {
    background-color: rgba(37, 211, 102, 0.1);
    font-weight: 600;
  }

  hr {
    border: none;
    border-top: 1px solid #ddd;
    margin: 12px 0;
  }
`;

const InputContainer = styled.form`
  padding: 16px 20px;
  background: #ffffff;
  border-top: 1px solid #e5ddd5;
  display: flex;
  gap: 12px;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  align-items: center;
  box-sizing: border-box;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  padding: 12px 20px;
  border: 1px solid #e5ddd5;
  border-radius: 25px;
  font-size: 16px; /* < 16px makes iOS Safari zoom on focus */
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
    Arial, sans-serif;
  font-weight: 400;
  outline: none;
  background: #ffffff;
  color: #000000;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  height: 40px;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none;

  &::placeholder {
    color: #999999;
    font-weight: 400;
  }

  &:focus {
    border-color: #25d366;
    box-shadow: 0 0 0 2px rgba(37, 211, 102, 0.1);
  }

  &:hover {
    border-color: #25d366;
  }
`;

const SendButton = styled.button`
  padding: 12px 24px;
  background: #25d366;
  color: white;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica,
    Arial, sans-serif;
  font-size: 14px;
  font-weight: 600;
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 2px 8px rgba(37, 211, 102, 0.3);
  height: 40px;
  flex-shrink: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background: #22c55e;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(37, 211, 102, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    box-shadow: 0 2px 6px rgba(156, 163, 175, 0.3);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover:not(:disabled) {
      transform: none;
    }
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 20px;
  background: #ffffff;
  border-radius: 18px 18px 18px 4px;
  align-self: flex-start;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  max-width: 120px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #999999;
    animation: concierge-typing 1.4s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: 0s;
    }
    &:nth-child(2) {
      animation-delay: 0.2s;
    }
    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    span {
      animation: none;
    }
  }
`;

// Animations live in a real global stylesheet rather than a wrapper <div>,
// which would otherwise sit between the flex containers and break the layout.
const ChatAnimations = createGlobalStyle`
  @keyframes concierge-typing {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-6px); opacity: 1; }
  }

  @keyframes concierge-slide-in {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;


const ChatWindow = () => {
  const config = useSyncExternalStore(subscribe, getConfig);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [guestName, setGuestName] = useState(null);
  const messageContainerRef = useRef(null);
  const nextMessageId = useRef(1);
  // The backend threads the conversation via conversation_id: absent on the
  // first message, then echoed back on every reply.
  const conversationIdRef = useRef(null);
  // Read inside async callbacks so a mid-flight reconfiguration is picked up.
  const configRef = useRef(config);
  configRef.current = config;

  const appendMessage = (message) => {
    setMessages((prev) => [...prev, message].slice(-MAX_MESSAGES));
  };

  useEffect(() => {
    const container = messageContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    const handleMessage = (event) => {
      // Only the parent page, and only on the configured allowlist, may
      // reconfigure this iframe. Without these checks any embedding page —
      // or any other window on an allowed origin, such as a popup — could
      // redirect the conversation and the token to an endpoint of its
      // choosing.
      if (event.source !== window.parent) return;
      if (!isParentOriginAllowed(event.origin)) return;

      if (event.data?.type === 'CONFIGURE_IFRAME') {
        const before = getConfig();
        updateConfigFromParent(event.data.config);
        const after = getConfig();
        // Only drop the conversation when the bot or backend actually
        // changed — the current conversation_id belongs to the old backend
        // and would be rejected there. A cosmetic change (title, welcome
        // text) must not restart an in-flight conversation.
        if (
          after.botId !== before.botId ||
          after.proxyUrl !== before.proxyUrl ||
          after.apiBaseUrl !== before.apiBaseUrl
        ) {
          conversationIdRef.current = null;
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const identifyTarget = resolveIdentifyEndpoint(config);
  const identifyUrl = identifyTarget?.url;

  useEffect(() => {
    setGuestName(null);
    if (!identifyUrl) return undefined;

    const controller = new AbortController();
    identifyGuest(
      { url: identifyUrl },
      config.email,
      config.identifyKey,
      controller.signal
    )
      .then((data) => setGuestName(extractGuestName(data)))
      .catch((error) => {
        if (controller.signal.aborted) return;
        // Personalisation only: stay anonymous. Never log the email.
        console.warn(
          '[concierge] Guest lookup failed:',
          error.response?.status ?? error.code ?? error.message
        );
      });

    return () => controller.abort();
  }, [identifyUrl, config.email, config.identifyKey]);

  const sendMessage = async () => {
    const messageText = inputValue.trim();
    if (!messageText || isLoading) return;

    const target = resolveEndpoint(configRef.current);
    if (!target) {
      appendMessage({
        id: nextMessageId.current++,
        text: '**Chat is unavailable.** No backend endpoint is configured.',
        sender: 'other',
        isAI: true
      });
      return;
    }

    // The direct API route cannot succeed without a token, so say that
    // plainly instead of letting a guaranteed 401 masquerade as an outage.
    if (target.useToken && !configRef.current.token) {
      appendMessage({
        id: nextMessageId.current++,
        text:
          '**This chat is not authorised yet.**\n\n' +
          'The widget must be embedded by an approved page that provides an ' +
          'access token — opening it directly will not work.',
        sender: 'other',
        isAI: true
      });
      return;
    }

    appendMessage({
      id: nextMessageId.current++,
      text: messageText,
      sender: 'user'
    });
    setInputValue('');
    setIsLoading(true);

    try {
      const payload = { question: messageText };
      if (conversationIdRef.current) {
        payload.conversation_id = conversationIdRef.current;
      }

      const data = await sendWithRetry(
        payload,
        target,
        configRef.current.token
      );
      const answer = extractAnswer(data);

      if (answer == null) {
        throw new Error('Response did not contain an assistant message');
      }

      conversationIdRef.current =
        extractConversationId(data) ?? conversationIdRef.current;

      appendMessage({
        id: nextMessageId.current++,
        text: answer,
        sender: 'other',
        isAI: true
      });
    } catch (error) {
      // Details stay in the console; the bubble shows nothing that would leak
      // endpoint URLs or status codes to the end user.
      console.error('[concierge] Request failed:', error);

      const status = error.response?.status;
      const text =
        status === 401 || status === 403
          ? '**This chat is not authorised.**\n\n' +
            'The access token is missing, expired, or not valid for this ' +
            'assistant. The embedding page needs to provide a valid token.'
          : "**Sorry — I couldn't reach the assistant just now.**\n\n" +
            'Please try again in a moment.';

      appendMessage({
        id: nextMessageId.current++,
        text,
        sender: 'other',
        isAI: true
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage();
  };

  // Implicit form submission on Enter is not reliable across every embedding
  // context, so handle the key directly as well.
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const welcome = {
    id: 'welcome',
    text:
      `## 💬 Concierge Chat\n\n` +
      (guestName ? `Hi ${escapeMarkdown(guestName)}! ` : '') +
      config.welcomeMessage,
    sender: 'other',
    isAI: true
  };

  return (
    <>
      <ChatAnimations />
      <ChatContainer>
        <MessageContainer ref={messageContainerRef}>
          {[welcome, ...messages].map((message) => {
            const bubbleProps = {
              $isSender: message.sender === 'user',
              $isAI: Boolean(message.isAI),
              $isTest: Boolean(message.isTest),
              style: { animation: 'concierge-slide-in 0.3s ease-out' }
            };

            return message.isAI || message.isTest ? (
              <MarkdownMessage key={message.id} {...bubbleProps}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Inside an iframe a plain link would navigate the frame
                    // itself, stranding the widget inside the host page.
                    a: ({ node, ...props }) => (
                      // eslint-disable-next-line jsx-a11y/anchor-has-content
                      <a {...props} target="_blank" rel="noopener noreferrer" />
                    )
                  }}
                >
                  {normaliseResponseText(message.text)}
                </ReactMarkdown>
              </MarkdownMessage>
            ) : (
              <Message key={message.id} {...bubbleProps}>
                {message.text}
              </Message>
            );
          })}

          {isLoading && (
            <TypingIndicator aria-label="Assistant is typing">
              <span />
              <span />
              <span />
            </TypingIndicator>
          )}
        </MessageContainer>

        <InputContainer onSubmit={handleSubmit}>
          <Input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me anything..."
            disabled={isLoading}
            maxLength={MAX_MESSAGE_LENGTH}
            aria-label="Message"
          />
          <SendButton type="submit" disabled={isLoading || !inputValue.trim()}>
            {isLoading ? 'Sending...' : 'Send'}
          </SendButton>
        </InputContainer>
      </ChatContainer>
    </>
  );
};

export default ChatWindow;
